import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { GraduationCap, ArrowRight } from "lucide-react";

export default function LoginPage() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      nav("/dashboard");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-5">
      {/* Left visual panel */}
      <div className="hidden lg:flex lg:col-span-3 relative dw-navy-bg overflow-hidden">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage: "url(https://images.unsplash.com/photo-1515263487990-61b07816b324?crop=entropy&cs=srgb&fm=jpg&q=85)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[var(--dw-navy)] via-[var(--dw-navy)]/85 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 text-[var(--dw-parchment)] w-full">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-link-login">
            <GraduationCap className="w-7 h-7 text-[var(--dw-gold)]" strokeWidth={1.5} />
            <div>
              <div className="font-serif text-xl">DW Training Academy</div>
              <div className="overline">NMLS SAFE MLO Preparation</div>
            </div>
          </Link>

          <div>
            <div className="overline mb-4">Est. Preparing licensed originators</div>
            <h2 className="font-serif text-4xl xl:text-5xl leading-[1.05] mb-6">
              The disciplined path to your NMLS SAFE MLO license.
            </h2>
            <p className="text-white/70 max-w-md text-[15px] leading-relaxed">
              Five modules. 188 exam-authentic questions. Live AI compliance tutoring built on
              Claude Sonnet 4.5. Every hour you invest here compounds toward the license.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-6 text-white/80">
            <Stat n="188" l="Practice Questions" />
            <Stat n="25 hr" l="Curriculum" />
            <Stat n="5" l="Test-Weighted Modules" />
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="lg:col-span-2 flex items-center justify-center p-8 lg:p-14 bg-[var(--dw-parchment)]">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-2">
            <GraduationCap className="w-6 h-6 text-[var(--dw-navy)]" strokeWidth={1.5} />
            <span className="font-serif text-lg text-[var(--dw-navy)]">DW Training Academy</span>
          </div>

          <div className="overline text-[var(--dw-gold-soft)] mb-3">{mode === "login" ? "Welcome back" : "Enroll today"}</div>
          <h1 className="font-serif text-4xl text-[var(--dw-navy)] mb-2">
            {mode === "login" ? "Sign in to continue." : "Create your account."}
          </h1>
          <p className="text-sm text-neutral-600 mb-8">
            {mode === "login" ? "Access your progress, quizzes, and tutor sessions." : "Track progress across every module and unlock the full 188-question bank."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <Field
                label="Full name"
                value={form.name}
                onChange={(v) => setForm({ ...form, name: v })}
                placeholder="Jane Underwriter"
                testid="input-name"
              />
            )}
            <Field
              label="Email"
              type="email"
              value={form.email}
              onChange={(v) => setForm({ ...form, email: v })}
              placeholder="you@firm.com"
              testid="input-email"
            />
            <Field
              label="Password"
              type="password"
              value={form.password}
              onChange={(v) => setForm({ ...form, password: v })}
              placeholder="Minimum 6 characters"
              testid="input-password"
            />

            {err && <div data-testid="auth-error" className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">{err}</div>}

            <button
              type="submit"
              disabled={busy}
              data-testid={mode === "login" ? "submit-login" : "submit-register"}
              className="dw-btn-primary w-full rounded-sm py-3 text-sm flex items-center justify-center gap-2 font-medium disabled:opacity-60"
            >
              {busy ? "Signing in…" : mode === "login" ? "Sign in" : "Create account"}
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </form>

          <div className="mt-6 text-sm text-neutral-600">
            {mode === "login" ? (
              <>
                Don't have an account?{" "}
                <button data-testid="toggle-register" onClick={() => setMode("register")} className="text-[var(--dw-navy)] font-medium underline underline-offset-4 hover:text-[var(--dw-gold-soft)]">Enroll</button>
              </>
            ) : (
              <>
                Already enrolled?{" "}
                <button data-testid="toggle-login" onClick={() => setMode("login")} className="text-[var(--dw-navy)] font-medium underline underline-offset-4 hover:text-[var(--dw-gold-soft)]">Sign in</button>
              </>
            )}
          </div>

          <div className="mt-10 pt-6 border-t border-[var(--dw-navy)]/10 text-xs text-neutral-500">
            Admin demo: <span className="font-mono-plex">admin@dwinvestment.com</span> / <span className="font-mono-plex">AdminDW2026!</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-[0.14em] text-neutral-500 mb-1.5">{label}</div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required
        data-testid={testid}
        className="w-full border border-[var(--dw-navy)]/15 rounded-sm px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[var(--dw-gold)]/40 focus:border-[var(--dw-navy)]/40 transition-colors"
      />
    </label>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="font-serif text-3xl text-[var(--dw-gold)]">{n}</div>
      <div className="overline mt-1 text-white/60">{l}</div>
    </div>
  );
}
